import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Table, Tag, Typography, EmptyState, Select } from '../../ui';
import { request } from '../../api/client';
import { formatIst } from '../../lib/time';
import { fmtQty, isRedundantSku } from '../../lib/format';
import { useServerSort } from '../../hooks/useServerSort';
import { useUrlString } from '../../hooks/useUrlState';
import { renderFacetOption } from '../../lib/facetOptions';

interface LowAlert {
  id: string;
  productId: string;
  sku: string | null;
  productName: string;
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  available: string;
  minLevel: string;
  openedAt: string | null;
  lastNotifiedAt: string | null;
}

/**
 * The durable "currently below minimum" list — warehouse-scoped server-side (a Warehouse Manager sees
 * only their warehouse(s); admins see all). This is the source of truth a newly-arrived WM/Admin can
 * pull regardless of who received past push notifications. Rows clear automatically when restocked.
 */
export function LowStockAlertsPage({ embedded }: { embedded?: boolean } = {}) {
  // Sorting is the backend's ORDER BY (see useServerSort) so every caller of this endpoint sees the
  // same order, and the sort survives a reload or a Back out of a row — it lives in the URL.
  const { sortState, onSortChange, sort } = useServerSort();
  /*
   * ONE SITE'S SHORTAGES, when that is the question being asked.
   *
   * The warehouse dashboard now says which site is carrying the shortage ("WH-PUNE 4 · WH-NSK 3"),
   * and a breakdown that cannot be opened is a dead end — so each of those counts arrives here as
   * `?wh=<code>`. It is a URL filter rather than component state so the back button returns to the
   * filtered list, and the control is shown only when there is more than one site to choose
   * between: a Warehouse Manager with one warehouse is not asked a question with one answer.
   */
  const [wh, setWh] = useUrlString<string | undefined>('wh', undefined);

  const { data, isLoading } = useQuery({
    queryKey: ['low-stock-alerts', sort],
    queryFn: () => request<LowAlert[]>({ url: '/stock/alerts/low-stock', params: sort ? { sort } : {} }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  // The endpoint returns every open alert in scope (no paging), so the site filter is applied here
  // rather than as a round trip — and the counts beside each option come from the same array.
  const all = data ?? [];
  const byWh = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of all) m.set(r.warehouseCode, (m.get(r.warehouseCode) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [all]);
  const rows = useMemo(() => (wh ? all.filter((r) => r.warehouseCode === wh) : all), [all, wh]);

  return (
    <div>
      {!embedded && (
        <>
          <Typography.Title level={1} style={{ marginBottom: 2 }}>Low Stock Alerts</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            Products currently at or below their minimum stock level. Resolves automatically when restocked.
          </Typography.Paragraph>
        </>
      )}
      {byWh.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface px-3 py-2.5 shadow-e1">
          <Select
            value={wh ?? 'ALL'} onChange={(v: string) => setWh(v === 'ALL' ? undefined : v)} size="sm" className="w-56"
            optionRender={renderFacetOption}
            options={[
              { value: 'ALL', label: 'All warehouses', count: all.length },
              ...byWh.map(([code, count]) => ({ value: code, label: code, count })),
            ]}
          />
          {wh && (
            <span className="type-caption text-subtle">
              Showing <b className="text-content">{rows.length}</b> of {all.length} open alerts ·{' '}
              <button type="button" onClick={() => setWh(undefined)} className="text-brand-700 hover:underline dark:text-brand-400">show every warehouse</button>
            </span>
          )}
        </div>
      )}
      <Card>
        <Table<LowAlert>
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          sortState={sortState}
          onSortChange={onSortChange}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} open` }}
          locale={{
            emptyText: wh
              // A filtered list that comes back empty must say WHY it is empty — "all good" under a
              // site filter reads as "the whole company is fine", which is the opposite of the truth
              // when another warehouse is ten products short.
              ? <EmptyState title={`Nothing below minimum in ${wh}.`} description={all.length ? `${all.length} product${all.length === 1 ? ' is' : 's are'} below minimum in other warehouses.` : undefined} />
              : <EmptyState title="No low-stock alerts — all good." />,
          }}
          columns={[
            {
              title: 'Product', dataIndex: 'productName', sorter: true,
              render: (v: string, r) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{v}</div>
                  {r.sku && !isRedundantSku(r.sku, v) && <div style={{ fontSize: 12, color: 'var(--gb-text-muted)' }}>{r.sku}</div>}
                </div>
              ),
            },
            { title: 'Warehouse', dataIndex: 'warehouseCode', width: 150, sorter: true, render: (v: string) => <Tag tone="blue">{v}</Tag> },
            { title: 'On hand', dataIndex: 'available', align: 'right' as const, width: 120, sorter: true, render: (v: any) => <b style={{ color: 'var(--color-danger-600)' }}>{fmtQty(Number(v))}</b> },
            { title: 'Minimum', dataIndex: 'minLevel', align: 'right' as const, width: 110, sorter: true, render: (v: any) => fmtQty(Number(v)) },
            {
              title: 'Short by', key: 'shortBy', align: 'right' as const, width: 110, sorter: true,
              render: (_: any, r) => { const s = Number(r.minLevel) - Number(r.available); return <Tag tone="red" style={{ margin: 0 }}>{fmtQty(s > 0 ? s : 0)}</Tag>; },
            },
            { title: 'Since', dataIndex: 'openedAt', width: 180, sorter: true, render: (v: string | null) => (v ? formatIst(v) : '—') },
          ]}
        />
      </Card>
    </div>
  );
}
